import { useMemo } from "react";
import groupBy from "lodash/groupBy";
import { convertDataToDataById } from "../helpers";
import { useOperationsData, useFieldsData, useFarmsData } from "../next/hooks";
import { EnrichedEntity } from "../models";
import {
  EnrichedFarmsData,
  EnrichedFieldsData,
  EnrichedOperationsData,
  EntityDataEnrichment,
} from "../models/useWith";
import { getActiveFields } from "./fields.helpers";

/**
 * Simple helper to iterate over some set of data with ids and add data
 * from an object with keys that might match those ids.
 */
const useEnrichedData = <
  OriginalData extends { id: string },
  EnrichingData extends Record<string, unknown>,
>(
  originalData: OriginalData[],
  enrichingData: EntityDataEnrichment<EnrichingData>,
): EnrichedEntity<OriginalData, EnrichingData>[] =>
  useMemo(
    () =>
      originalData.map((datum) => {
        const addon = enrichingData[datum.id];
        return {
          ...datum,
          enrichedData: addon,
        };
      }),
    [originalData, enrichingData],
  );

/**
 * Returns the data from useFarms(), but enriched by whatever you pass in.
 * Enriching data will be stored in the `enrichedData` property so you don't accidentally overwrite keys.
 * @param enrichingData The data you'll be adding to useFarms(). Your keys must be farm ids.
 */
export const useFarmsWith = <EnrichingData extends Record<string, unknown>>(
  enrichingData: EntityDataEnrichment<EnrichingData>,
): EnrichedFarmsData<EnrichingData> => {
  const { farms: originalFarms } = useFarmsData();
  const farms = useEnrichedData(originalFarms, enrichingData);
  const farmsById = useMemo(() => convertDataToDataById(farms), [farms]);
  return useMemo(
    () => ({
      farms,
      farmsById,
    }),
    [farms, farmsById],
  );
};

/**
 * Returns the data from useFields(), but enriched by whatever you pass in.
 * Enriching data will be stored in the `enrichedData` property so you don't accidentally overwrite keys.
 * @param enrichingData The data you'll be adding to useFields(). Your keys must be field ids.
 */
export const useFieldsWith = <EnrichingData extends Record<string, unknown>>(
  enrichingData: EntityDataEnrichment<EnrichingData>,
): EnrichedFieldsData<EnrichingData> => {
  const { fields: originalFields } = useFieldsData();

  const allFields = useEnrichedData(originalFields, enrichingData);

  const allFieldsById = useMemo(
    () => convertDataToDataById(allFields),
    [allFields],
  );

  const allFieldsByFarmId = useMemo(
    () => groupBy(allFields, "farmId"),
    [allFields],
  );

  const activeFields = useMemo(() => {
    return getActiveFields({ fields: allFields });
  }, [allFields]);

  return useMemo(
    () => ({
      fields: allFields,
      fieldsById: allFieldsById,
      fieldsByFarmId: allFieldsByFarmId,
      activeFields,
    }),
    [activeFields, allFields, allFieldsById, allFieldsByFarmId],
  );
};

/**
 * Returns the data from useOperations(), but enriched by whatever you pass in.
 * Enriching data will be stored in the `enrichedData` property so you don't accidentally overwrite keys.
 * @param enrichingData The data you'll be adding to useOperations(). Your keys must be operation ids.
 */
export const useOperationsWith = <
  EnrichingData extends Record<string, unknown>,
>(
  enrichingData: EntityDataEnrichment<EnrichingData>,
): EnrichedOperationsData<EnrichingData> => {
  const operationsData = useOperationsData();
  const operations = useEnrichedData(operationsData.operations, enrichingData);
  const operationsById = useMemo(
    () => convertDataToDataById(operations),
    [operations],
  );
  return useMemo(
    () => ({ operations, operationsById }),
    [operations, operationsById],
  );
};
